import { createContext, FunctionComponent, PropsWithChildren, useContext } from 'react';
import { getBudgetFiltersFromUrl } from '../../../helpers/queryStringHelpers/getBudgetFiltersFromUrl';
import { getPropertyConfigFiltersFromUrl } from '../../../helpers/queryStringHelpers/getPropertyConfigFromUrl';
import {
  getLocationsFromUrl,
  getFindSimilarImageFromUrl,
} from '../../../helpers/queryStringHelpers/queryStringHelpers';
import {
  countActiveLocationFilters,
  countActiveBudgetFilters,
  countActivePropertyFilters,
} from '../../../helpers/searchSlugDefaultChecks';
import { OptionalPropertyFilters } from '../../../types';
import { SearchLandingPageFragment } from '../../types';

export type SearchLandingPageData = SearchLandingPageFragment;
type SearchLandingPage = {
  searchLandingPageData: SearchLandingPageData;
  searchQuery: string;
  activeFilters: {
    activeLocationFilters: number;
    activePropertyFilters: number;
    activeBudgetFilters: number;
    activeFindSimilarImage: { imageId: string; imageName?: string } | null;
  };
};
export const SearchLandingPageContext = createContext<SearchLandingPage | undefined>(undefined);

export const useSearchLandingPageContext = (): SearchLandingPage => {
  const context = useContext(SearchLandingPageContext);
  if (context === undefined) {
    throw new Error(
      'useSearchLandingPageContext must be used within a SearchLandingPageContext.Provider',
    );
  }
  return context;
};

export type SearchLandingPageContextProviderProps = {
  searchLandingPageData: SearchLandingPageData;
};

export const SearchLandingPageContextProvider: FunctionComponent<
  PropsWithChildren<SearchLandingPageContextProviderProps>
> = ({ children, searchLandingPageData }) => {
  const searchQuery = searchLandingPageData.searchQuery || '';

  const {
    propertyStatus,
    propertyTypes,
    minBedrooms,
    minBathrooms,
    minLandSize,
    indoorFeatures,
    outdoorFeatures,
    ecoFriendlyFeatures,
    minCarSpaces,
    keywords,
  } = getPropertyConfigFiltersFromUrl(searchQuery);

  const optionalPropertyFilters: OptionalPropertyFilters = {
    propertyTypes,
    minBedrooms,
    minBathrooms,
    minLandSize,
    indoorFeatures,
    outdoorFeatures,
    ecoFriendlyFeatures,
    minCarSpaces,
    keywords,
  };

  const locations = getLocationsFromUrl(searchQuery);

  const activeFilters = {
    activeLocationFilters: countActiveLocationFilters(locations),
    activeBudgetFilters: countActiveBudgetFilters(getBudgetFiltersFromUrl(searchQuery)),
    activePropertyFilters: countActivePropertyFilters({
      propertyStatus,
      optionalPropertyFilters,
    }),
    activeFindSimilarImage: getFindSimilarImageFromUrl(searchQuery),
  };

  return (
    <SearchLandingPageContext.Provider
      value={{ searchLandingPageData, searchQuery, activeFilters }}
    >
      {children}
    </SearchLandingPageContext.Provider>
  );
};
